import * as React from 'react'
import type { ChatMessage } from './types'

export interface AgentQuestion {
  prompt: string
  options: string[]
}

const QUESTION_BLOCK_PATTERN = /<question>([\s\S]*?)<\/question>/gi

function extractOptionsFromQuestionBody(body: string): { prompt: string; options: string[] } {
  const options: string[] = []
  const optionPattern = /<option\b[^>]*>([\s\S]*?)<\/option>/gi
  let optionMatch: RegExpExecArray | null
  while ((optionMatch = optionPattern.exec(body)) !== null) {
    const option = optionMatch[1].replace(/\s+/g, " ").trim()
    if (option) options.push(option)
  }
  const prompt = body
    .replace(/<option\b[^>]*>[\s\S]*?<\/option>/gi, "")
    .replace(/\s+/g, " ")
    .trim()
  return { prompt, options }
}

/** Parse `<question>` / `<option>` blocks from an assistant reply. */
export function parseAgentQuestions(text: string): AgentQuestion[] {
  const questions: AgentQuestion[] = []
  const blockPattern = /<question>([\s\S]*?)<\/question>/gi
  let match: RegExpExecArray | null
  while ((match = blockPattern.exec(text)) !== null) {
    const { prompt, options } = extractOptionsFromQuestionBody(match[1])
    if (prompt && options.length >= 2) {
      questions.push({ prompt, options })
    }
  }
  return questions
}

/**
 * Replace each `<question>…</question>` block with its prompt text so the
 * bubble shows the question without raw tags; options render as buttons.
 */
export function stripAgentQuestions(text: string): string {
  return text
    .replace(QUESTION_BLOCK_PATTERN, (_full, body: string) => {
      return extractOptionsFromQuestionBody(String(body)).prompt
    })
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

/** True when this assistant message still awaits a choice (no later user turn). */
export function isQuestionInteractive(
  messages: ChatMessage[],
  messageId: string,
  isSending: boolean
): boolean {
  if (isSending) return false
  const index = messages.findIndex(message => message.id === messageId)
  if (index < 0) return false
  return !messages.slice(index + 1).some(message => message.role === "user")
}

export function AgentQuestionChoices({
  questions,
  disabled,
  onChoose,
}: {
  questions: AgentQuestion[]
  disabled: boolean
  onChoose: (option: string) => void
}) {
  if (questions.length === 0) return null
  return (
    <div className="model-editor-chat-questions" role="group" aria-label="Multiple choice answers">
      {questions.map((question, questionIndex) => (
        <div key={`q-${questionIndex}`} className="model-editor-chat-question">
          <div className="model-editor-chat-question-options">
            {question.options.map((option, optionIndex) => (
              <button
                key={`q-${questionIndex}-o-${optionIndex}`}
                type="button"
                className="model-editor-chat-question-option"
                disabled={disabled}
                onClick={() => onChoose(option)}
              >
                {option}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

